import { AlertTriangle } from 'lucide-react'

import { promotionChannelLabels } from './promotion-form'

import { Badge } from '@/app/components/ui/badge'
import type {
  PromotionConflict,
  PromotionOption,
} from '@/app/services/promotion.service'

interface PromotionConflictDetailsProps {
  conflicts: PromotionConflict[]
  products?: PromotionOption[]
  sucursals?: PromotionOption[]
}

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatDate(value: string | null) {
  if (!value) return 'Sin límite registrado'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date)
}

export function PromotionConflictDetails({
  conflicts,
  products = [],
  sucursals = [],
}: PromotionConflictDetailsProps) {
  const productNames = new Map(
    products.map((product) => [product.id, product.name]),
  )
  const sucursalNames = new Map(
    sucursals.map((sucursal) => [sucursal.id, sucursal.name]),
  )

  if (conflicts.length === 0) return null

  return (
    <div
      className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold">
            Conflicto con otra estrategia de precio
          </p>
          <p className="text-sm">
            Ajusta la vigencia, el canal, el alcance o los productos antes de
            continuar.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {conflicts.map((conflict, index) => {
          const title =
            conflict.strategy === 'promotion'
              ? `${conflict.name || 'Promoción'}${conflict.promotion_id ? ` (#${conflict.promotion_id})` : ''}`
              : `Precio por volumen${conflict.quantity_tier_id ? ` (#${conflict.quantity_tier_id})` : ''}`
          const branchLabels = conflict.sucursal_ids.map((id) =>
            id === '*'
              ? 'Todas las sucursales'
              : sucursalNames.get(id) || `Sucursal #${id}`,
          )

          return (
            <div
              key={`${conflict.strategy}-${conflict.promotion_id ?? conflict.quantity_tier_id ?? index}`}
              className="rounded-md border border-amber-200 bg-white/70 p-3 text-sm"
            >
              <p className="font-medium">{title}</p>
              <p className="mt-1 text-xs text-amber-900">
                {formatDate(conflict.starts_at)} —{' '}
                {formatDate(conflict.ends_at)}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {conflict.product_ids.map((id) => (
                  <Badge key={`product-${id}`} variant="secondary">
                    {productNames.get(id) || `Producto #${id}`}
                  </Badge>
                ))}
                {branchLabels.map((label) => (
                  <Badge key={`branch-${label}`} variant="outline">
                    {label}
                  </Badge>
                ))}
                {conflict.channels.map((channel) => (
                  <Badge key={`channel-${channel}`} variant="outline">
                    {promotionChannelLabels[channel]}
                  </Badge>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
